/**
 * LgpdCascadePreview — Preview da cascade chain de audit via AuditTreeView.
 *
 * D-B defense-in-depth: hasSensitive passado quando caller NÃO tem
 * `audit-events-read-sensitive` — AuditTreeView exibe placeholder neutro
 * para nós sensíveis sem vazar dados.
 *
 * Consome W1.4 AuditController `GET /v1/audit-events/cascade/:entityId`.
 * ADR-107 cascade chain reuso padrão.
 *
 * P17 (Auditabilidade nativa): preview visual = audit trail canônico.
 */

import { AuditTreeView, CapabilityLockedBanner } from '@lume/ui-components';
import { useEffect, useState } from 'react';

import { useCapability } from '../../../hooks/use-capability';
import { api } from '../../../lib/api-client';

import type { AuditCascadeResponse } from './types';
import type { AuditTreeNode } from '@lume/ui-components';

interface LgpdCascadePreviewProps {
  entityId: string;
  /** Shown in heading — e.g. "Solicitação lgpd_req_001" */
  entityLabel?: string;
}

export function LgpdCascadePreview({
  entityId,
  entityLabel,
}: LgpdCascadePreviewProps): JSX.Element {
  const canReadSensitive = useCapability('audit-events-read-sensitive');
  const canReadAudit = useCapability('audit-events-read');

  const [cascade, setCascade] = useState<AuditCascadeResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!canReadAudit || entityId === '') return;
    setIsLoading(true);
    setIsError(false);
    api
      .getAuditCascade(entityId)
      .then((res) => {
        setCascade(res);
      })
      .catch(() => {
        setIsError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [entityId, canReadAudit]);

  // D-B own gate — não confiar em parent gate
  if (!canReadAudit) {
    return (
      <CapabilityLockedBanner
        capability="audit-events-read"
        tierMin="team"
        fallbackMessage="A trilha de auditoria é restrita a perfis com acesso à auditoria (disponível a partir do pacote Team)."
      />
    );
  }

  if (isLoading) {
    return (
      <div
        aria-busy="true"
        aria-label="Carregando trilha de auditoria…"
        style={{ padding: 16, color: '#9ca3af', fontSize: 12 }}
      >
        Carregando trilha de auditoria…
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{ padding: 16, color: '#ef4444', fontSize: 12 }}>
        Erro ao carregar a trilha de auditoria. Tente recarregar.
      </div>
    );
  }

  if (cascade === null || cascade.events.length === 0) {
    return (
      <div style={{ padding: 16, color: '#9ca3af', fontSize: 12, fontStyle: 'italic' }}>
        Nenhum evento de cascata registrado para {entityLabel ?? entityId}.
      </div>
    );
  }

  // Convert AuditEventWireItem[] → AuditTreeNode[] (flat → tree via correlationId)
  const nodes = buildAuditTree(cascade.events);

  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: '#6b7280',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          marginBottom: 8,
        }}
      >
        Trilha de auditoria · {cascade.totalCount} evento(s)
      </div>

      {/* D-B: hasSensitive={!canReadSensitive} — AuditTreeView own gate */}
      <AuditTreeView
        nodes={nodes}
        hasSensitive={!canReadSensitive}
        sensitivePlaceholder="Detalhes restritos a perfis com acesso à auditoria sensível (disponível a partir do pacote Professional)"
        ariaLabel={`Trilha de auditoria para ${entityLabel ?? entityId}`}
        expandedIds={nodes.length > 0 ? [nodes[0]?.id ?? ''] : []}
      />

      {/* Info sobre mascaramento para callers sem sensitive */}
      {!canReadSensitive && (
        <div
          style={{
            marginTop: 8,
            padding: '6px 10px',
            background: '#fef9c3',
            border: '1px solid #fde68a',
            borderRadius: 4,
            fontSize: 10,
            color: '#92400e',
          }}
        >
          Payloads sensíveis mascarados. Requer <code>audit-events-read-sensitive</code> (Pro+) para
          visualização integral.
        </div>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * buildAuditTree — flat events → AuditTreeNode[].
 *
 * Strategy: agrupar por correlationId; eventos sem correlationId = raiz.
 * Ordem: cronológica por createdAt.
 */
function buildAuditTree(events: AuditCascadeResponse['events']): AuditTreeNode[] {
  const sorted = [...events].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  const roots: AuditTreeNode[] = [];
  const byCorrelation = new Map<string, AuditTreeNode[]>();

  for (const event of sorted) {
    const node: AuditTreeNode = {
      id: event.id,
      label: event.eventType,
      timestamp: event.createdAt,
      ...(event.actorUserId !== null ? { actor: event.actorUserId } : {}),
      sensitive: event.severity === 'high' || event.severity === 'forensic',
      operationType: resolveOperationType(event.eventType) ?? 'generic',
      children: [],
    };

    if (event.correlationId !== null && event.correlationId !== event.id) {
      const siblings = byCorrelation.get(event.correlationId) ?? [];
      siblings.push(node);
      byCorrelation.set(event.correlationId, siblings);
    } else {
      roots.push(node);
    }
  }

  // Attach children to parent nodes
  for (const root of roots) {
    const children = byCorrelation.get(root.id) ?? [];
    root.children = children;
  }

  return roots;
}

function resolveOperationType(eventType: string): AuditTreeNode['operationType'] {
  if (eventType.includes('anon')) return 'anonymization';
  if (eventType.includes('delet') || eventType.includes('hard-delete')) return 'deletion';
  if (eventType.includes('redact')) return 'redaction';
  if (eventType.includes('resolv') || eventType.includes('conflict')) return 'resolution';
  return 'generic';
}
